import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";

interface GradientPageHeaderProps {
  title: string;
  titleHighlight?: string; // The word to highlight in yellow
  description?: string;
  children?: React.ReactNode; // For buttons or other content
  rightContent?: React.ReactNode; // Content to render top-right (e.g., CTA button)
  belowDescription?: React.ReactNode; // New slot rendered just under the subtitle
  // Search functionality props
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  // Filter functionality props
  showFilters?: boolean;
  categories?: string[];
  selectedCategory?: string;
  onCategoryChange?: (category: string) => void;
}

const getCategoryFilterEmoji = (category: string) => {
  switch (category) {
    case "All":
      return "✨";
    case "Sales":
      return "💰";
    case "Marketing":
      return "📈";
    case "Daily work":
      return "🧑‍💻";
    case "Customer Engagement":
      return "🤝";
    case "Operations":
      return "🛠️";
    case "Legal":
      return "⚖️";
    case "Finance":
      return "💵";
    case "HR":
      return "👥";
    default:
      return "🤖";
  }
};

export const GradientPageHeader: React.FC<GradientPageHeaderProps> = ({
  title,
  titleHighlight,
  description,
  children,
  rightContent,
  belowDescription,
  showSearch = false,
  searchQuery = "",
  onSearchChange,
  showFilters = false,
  categories = [],
  selectedCategory = "All",
  onCategoryChange,
}) => {
  const [placeholderIndex, setPlaceholderIndex] = useState<number>(0);

  // Rotating placeholder texts
  const placeholderTexts = [
    "Plan a project",
    "Respond to an email",
    "Write social media content",
    "Create marketing copy",
    "Draft a proposal",
    "Generate ideas",
  ];

  // Rotate placeholder text every 3 seconds
  useEffect(() => {
    if (showSearch) {
      const interval = setInterval(() => {
        setPlaceholderIndex((prev) => (prev + 1) % placeholderTexts.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [showSearch, placeholderTexts.length]);

  // Split title to highlight specific word
  const titleParts = titleHighlight ? title.split(titleHighlight) : [title];

  return (
    <div className="relative w-full">
      <div
        className="absolute inset-0 w-screen bg-gradient-to-b from-amber-100 via-amber-50 to-white"
        style={{ left: "50%", transform: "translateX(-50%)" }}
      ></div>
      <div className="relative px-6 sm:px-12 py-4 pt-20 flex flex-col justify-center items-center overflow-hidden">
        {rightContent && (
          <div className="absolute right-6 top-6 z-20">{rightContent}</div>
        )}
        {/* WhatsApp-style Background Pattern Overlay with gradient mask */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: (() => {
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
              const backgroundUrl = `url(${supabaseUrl}/storage/v1/object/public/image_library/chat_backgrounds/Whatsapp-Style-Background.svg)`;
              return backgroundUrl;
            })(),
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            opacity: 0.06,
            zIndex: 1,
            pointerEvents: "none",
            filter: "sepia(0.3) saturate(0.8) hue-rotate(20deg)",
            mask: "linear-gradient(to bottom, black, rgba(0,0,0,0.5), transparent)",
            WebkitMask:
              "linear-gradient(to bottom, black, rgba(0,0,0,0.5), transparent)",
          }}
        />

        <div className="text-center relative z-10">
          <h1 className="text-2xl sm:text-[32px] font-bold mb-4 tracking-tight leading-tight">
            {titleHighlight ? (
              <>
                {titleParts[0]}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-500 to-amber-500">
                  {titleHighlight}
                  {titleParts[1] && titleParts[1].charAt(0) === "?" ? "?" : ""}
                </span>
                {titleParts[1] && titleParts[1].charAt(0) === "?"
                  ? titleParts[1].slice(1)
                  : titleParts[1]}
              </>
            ) : (
              title
            )}
          </h1>

          {description && (
            <p className="text-gray-600 mb-10 text-base max-w-2xl mx-auto">
              {description}
            </p>
          )}

          {belowDescription && <div className="mb-8">{belowDescription}</div>}

          {/* Search Bar */}
          {showSearch && (
            <div className="flex justify-center mb-6">
              <div className="relative w-full max-w-[38rem] group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-400/50 via-yellow-500/60 to-amber-400/50 rounded-full blur-md opacity-75 transition-all duration-300 group-focus-within:opacity-100 group-focus-within:blur-lg"></div>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 transition-colors group-focus-within:text-amber-500" />
                  <Input
                    type="text"
                    placeholder={placeholderTexts[placeholderIndex]}
                    value={searchQuery}
                    onChange={(e) => onSearchChange?.(e.target.value)}
                    className="pl-12 pr-4 py-3 h-12 text-base bg-white rounded-full border border-gray-100 shadow-sm transition-all duration-300 focus:ring-1 focus:ring-amber-400/30 focus:border-amber-200 focus:shadow-xl hover:border-gray-200"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Category Filter Tags */}
          {showFilters &&
            categories.length > 0 &&
            (() => {
              const desiredOrder = [
                "Marketing",
                "Sales",
                "Daily work",
                "Customer Engagement",
                "Operations",
                "Finance",
                "HR",
                "Legal",
              ];
              const ordered =
                categories[0] === "All"
                  ? [
                      "All",
                      ...desiredOrder.filter((c) => categories.includes(c)),
                      ...categories.filter(
                        (c) => c !== "All" && !desiredOrder.includes(c),
                      ),
                    ]
                  : [
                      ...desiredOrder.filter((c) => categories.includes(c)),
                      ...categories.filter((c) => !desiredOrder.includes(c)),
                    ];
              return (
                <div className="flex flex-wrap justify-center gap-3 max-w-[860px] mx-auto">
                  {ordered.map((category) => {
                    const isSelected = selectedCategory === category;

                    return (
                      <Button
                        key={category}
                        onClick={() => onCategoryChange?.(category)}
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        className={`flex items-center gap-2 rounded-full px-4 py-2 transition-all duration-300 ${
                          isSelected
                            ? `${
                                category === "Marketing"
                                  ? "bg-blue-500 hover:bg-blue-600"
                                  : category === "Sales"
                                    ? "bg-green-500 hover:bg-green-600"
                                    : category === "Daily work"
                                      ? "bg-amber-500 hover:bg-amber-600"
                                      : category === "Customer Engagement"
                                        ? "bg-cyan-500 hover:bg-cyan-600"
                                        : category === "Operations"
                                          ? "bg-purple-500 hover:bg-purple-600"
                                          : category === "Legal"
                                            ? "bg-rose-500 hover:bg-rose-600"
                                            : category === "Finance"
                                              ? "bg-indigo-500 hover:bg-indigo-600"
                                              : category === "HR"
                                                ? "bg-emerald-600 hover:bg-emerald-700"
                                                : "bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600"
                              } text-white shadow-lg transform scale-105`
                            : "hover:bg-gray-50 hover:scale-105 hover:shadow-md border-gray-200"
                        }`}
                      >
                        <span className="text-lg">
                          {getCategoryFilterEmoji(category)}
                        </span>
                        <span className="font-medium">{category}</span>
                      </Button>
                    );
                  })}
                </div>
              );
            })()}

          {children && (
            <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
